import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Chip,
  Tooltip
} from '@mui/material';
import {
  Close as CloseIcon,
  Schedule as ScheduleIcon,
  AccessTime as TimeIcon,
  PlayArrow as PlayIcon,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { configurationService } from '../../services/configurationService';
import { cronService } from '../../services/cronService';
import { formatarDataHoraSegundos } from '../../utils/formatToView';
import { toast } from 'react-toastify';

interface CronExecutionsModalProps {
  open: boolean;
  onClose: () => void;
}

const CRON_EXECUTAVEIS = [
  'lastExecCronLembretes',
  'lastExecCronAtualizarFaturas',
  'lastExecCronAniversario',
  'lastExecCronBackup',
  'lastExecCronTransmissaoNfcom',
  'lastExecCronLimpeza',
  'lastExecCronEnviarMensagens',
  'lastExecCronGeracaoFaturas',
  'lastExecCronSuspensao',
  'lastExecCronBloqueio',
  'lastExecCronDesbloqueio'
];

const CronExecutionsModal: React.FC<CronExecutionsModalProps> = ({
  open,
  onClose
}) => {
  const [executando, setExecutando] = useState<string | null>(null);

  const {
    data: configurations,
    isLoading,
    error,
    refetch
  } = useQuery({
    queryKey: ['cron-executions'],
    queryFn: async () => {
      const allConfigurations = await configurationService.getAllConfigurations();
      // Filtrar apenas configurações da categoria 'cron'
      return allConfigurations.filter(config => config.categoria === 'cron');
    },
    enabled: open, // Só executa quando o modal está aberto
    refetchOnMount: true,
    staleTime: 0
  });

  const handleClose = () => {
    onClose();
  };

  const handleExecutar = async (chave: string) => {
    if (!CRON_EXECUTAVEIS.includes(chave)) return;
    setExecutando(chave);
    try {
      const res = await cronService.execute(chave);
      if (res?.success) {
        toast.success('Cron executado com sucesso');
        refetch();
      } else {
        toast.error(res?.message || 'Erro ao executar cron');
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message || err?.message || 'Erro ao executar cron');
    } finally {
      setExecutando(null);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { height: '55vh', maxHeight: '60vh' }
      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={1}>
            <ScheduleIcon color="primary" />
            <Typography variant="h6">
              Últimas Execuções do Cron
            </Typography>
          </Box>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Erro ao carregar execuções do cron: {(error as any)?.message}
          </Alert>
        )}

        {isLoading ? (
          <Box display="flex" justifyContent="center" p={1}>
            <CircularProgress />
          </Box>
        ) : configurations && configurations.length > 0 ? (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell width={60}></TableCell>
                  <TableCell>Descrição</TableCell>
                  <TableCell>Última execução</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {configurations
                  .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
                  .map((config) => {
                    const podeExecutar = CRON_EXECUTAVEIS.includes(config.chave);
                    const estaExecutando = executando === config.chave;
                    return (
                      <TableRow key={config.id} hover>
                        <TableCell padding="checkbox">
                          {podeExecutar ? (
                            <Tooltip title="Executar manualmente">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => handleExecutar(config.chave)}
                                disabled={estaExecutando}
                              >
                                {estaExecutando ? (
                                  <CircularProgress size={20} />
                                ) : (
                                  <PlayIcon fontSize="small" />
                                )}
                              </IconButton>
                            </Tooltip>
                          ) : (
                            <Tooltip title="Execução manual não disponível">
                              <span>
                                <IconButton size="small" disabled>
                                  <PlayIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          <Typography variant="body2" fontWeight="medium">
                            {config.descricao || config.chave}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          <Box display="flex" alignItems="center" gap={1}>
                            <TimeIcon color="action" fontSize="small" />
                            <Chip
                              label={config.valor ? formatarDataHoraSegundos(config.valor) : 'Nenhuma execução registrada'}
                              size="small"
                              color="default"
                              variant="outlined"
                            />
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Box textAlign="center" py={4}>
            <ScheduleIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              Nenhuma execução de cron encontrada
            </Typography>
            <Typography variant="body2" color="text.secondary">
              As execuções do cron aparecerão aqui quando estiverem configuradas no sistema.
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} variant="outlined">
          Fechar
        </Button>
        <Button onClick={() => refetch()} variant="contained" startIcon={<ScheduleIcon />}>
          Atualizar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CronExecutionsModal; 